import { useState, useCallback, useMemo, useEffect } from "react";
import { EmailAttachment, EmailData, SubmitBundleModalProps } from "./types";

export const useSubmitBundleModal = (props: SubmitBundleModalProps) => {
  const { transactions, custodianName, custodianEmail, generatedDocuments, onSend, onClose, matchedFlow } = props;

  // Generate default values
  const defaultSubject = `Transaction Instructions - ${transactions.length} Transaction${transactions.length > 1 ? "s" : ""} for Processing`;
  const defaultBody = `Dear Team,

Please find attached the transaction instructions for ${transactions.length} transaction${transactions.length > 1 ? "s" : ""} that require processing.

All necessary documentation has been included. Please confirm receipt and processing of these instructions at your earliest convenience.

If you have any questions or require additional information, please don't hesitate to contact us.

Best regards,
Confolio Wealth Management Team`;

  // Initialize with values from matched flow if available
  const [to, setTo] = useState(matchedFlow?.email_action?.to || custodianEmail || "");
  const [cc, setCc] = useState(matchedFlow?.email_action?.cc || "");
  const [subject, setSubject] = useState(matchedFlow?.email_action?.subject || defaultSubject);
  const [body, setBody] = useState(matchedFlow?.email_action?.body || defaultBody);

  const [attachments, setAttachments] = useState<EmailAttachment[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [hasAutoAttached, setHasAutoAttached] = useState(false);

  // Update form when matchedFlow changes
  useEffect(() => {
    if (matchedFlow?.email_action) {
      setTo(matchedFlow.email_action.to || custodianEmail || "");
      setCc(matchedFlow.email_action.cc || "");
      setSubject(matchedFlow.email_action.subject || defaultSubject);
      setBody(matchedFlow.email_action.body || defaultBody);
    }
  }, [matchedFlow, custodianEmail, defaultSubject, defaultBody]);

  // Initialize with generated documents
  const availableDocuments = useMemo(() => {
    return generatedDocuments.map((doc) => ({
      id: doc.identifier,
      name: doc.file_name || doc.name,
      size: 0, // Size not available from the stored documents
      type: "generated" as const,
      documentId: doc.identifier,
    }));
  }, [generatedDocuments]);

  // Auto-attach document from matched flow template_action
  useEffect(() => {
    if (matchedFlow?.template_action && !hasAutoAttached && generatedDocuments.length > 0) {
      const templateAction = matchedFlow.template_action;

      // If it's a generated document, find it and attach
      if (templateAction.source_type === "generated_document" && templateAction.document_id) {
        const docToAttach = generatedDocuments.find((d) => d.identifier === templateAction.document_id);
        if (docToAttach) {
          const attachment: EmailAttachment = {
            id: crypto.randomUUID(),
            name: docToAttach.file_name || docToAttach.name,
            size: 0,
            type: "generated",
            documentId: docToAttach.identifier,
          };
          setAttachments([attachment]);
          setHasAutoAttached(true);
        }
      }
      // If it's a template, we could generate it - for now just log
      // In the future, could call generateFilledPDF with the template_id
    }
  }, [matchedFlow, generatedDocuments, hasAutoAttached]);

  const addGeneratedDocument = useCallback((doc: EmailAttachment) => {
    setAttachments((prev) => {
      if (prev.some((a) => a.documentId === doc.documentId)) {
        return prev;
      }
      return [...prev, doc];
    });
  }, []);

  const addLocalFile = useCallback((file: File) => {
    const attachment: EmailAttachment = {
      id: crypto.randomUUID(),
      name: file.name,
      size: file.size,
      type: "local",
      file,
    };
    setAttachments((prev) => [...prev, attachment]);
  }, []);

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files) {
        Array.from(files).forEach(addLocalFile);
      }
      event.target.value = "";
    },
    [addLocalFile]
  );

  const handleSend = useCallback(async () => {
    if (!to.trim()) return;

    setIsSending(true);
    try {
      const emailData: EmailData = {
        to: to.trim(),
        cc: cc.trim(),
        subject: subject.trim(),
        body,
        attachments,
      };
      await onSend(emailData);
      onClose();
    } finally {
      setIsSending(false);
    }
  }, [to, cc, subject, body, attachments, onSend, onClose]);

  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return "N/A";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  }, []);

  const canSend = useMemo(() => {
    return to.trim().length > 0 && subject.trim().length > 0 && !isSending;
  }, [to, subject, isSending]);

  return {
    // Form state
    to,
    setTo,
    cc,
    setCc,
    subject,
    setSubject,
    body,
    setBody,
    attachments,
    availableDocuments,
    isSending,
    canSend,
    custodianName,
    matchedFlow,

    // Actions
    addGeneratedDocument,
    addLocalFile,
    removeAttachment,
    handleFileUpload,
    handleSend,
    formatFileSize,
  };
};
